import { useStoreStock } from '@gozoki/api'
import type { PalmitoTransitLine } from '@gozoki/api-types'
import { includesLowerCase, priceToString } from '@gozoki/tools'
import { Paper, TextField } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import StockCard from '~/components/communication/StockCard'
import SimpleDataGrid from '~/components/containers/SimpleDataGrid'
import { StoreDetailsLayout } from '~/components/routes/stores/StoreDetailsLayout'
import { stockColDef, transitColDefs } from '~/components/routes/stores/stocks/stockGrids'
import { STORE_TAB_PERMISSIONS } from '~/components/routes/stores/tabs'
import { withRoutePermissions } from '~/utils/rbac'
import { DataGridOverrides } from '~/utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/stores/$storeId/stocks/')({
  staticData: {
    permission: STORE_TAB_PERMISSIONS.stocks,
  },
  component: StoreStocksTab,
  params: {
    parse: z.object({ storeId: z.coerce.number() }).parse,
  },
})
withRoutePermissions(Route)

// TEMP PALMITO/MOUGUERRE — rendu du stock en transit Mouguerre → Port-Vieux (#1085).

/** Une borne ISO du service, à l'heure de Paris et en français. */
const inParis = (iso: string) => DateTime.fromISO(iso).setZone('Europe/Paris').setLocale('fr')

const DAY_FORMAT = 'ccc dd/LL' // « sam. 17/10 »
const TIME_FORMAT = 'HH:mm'

/** « flashés le sam. 17/10 entre 01:10 et 01:44 », ou sur deux jours ; rien sans bornes. */
const flashedSubtitle = (line: PalmitoTransitLine): string | undefined => {
  if (line.between === null) {
    return undefined
  }
  const from = inParis(line.between.from)
  const to = inParis(line.between.to)
  if (!from.isValid || !to.isValid) {
    return undefined
  }
  if (from.toISODate() === to.toISODate()) {
    return `flashés le ${from.toFormat(DAY_FORMAT)} entre ${from.toFormat(TIME_FORMAT)} et ${to.toFormat(TIME_FORMAT)}`
  }
  const dayAndTime = `${DAY_FORMAT} ${TIME_FORMAT}`
  return `flashés entre le ${from.toFormat(dayAndTime)} et le ${to.toFormat(dayAndTime)}`
}

/** « dernier scan à Mouguerre le 17/10 12:26 » (borne haute) ; rien sans bornes. */
const lastScanSubtitle = (line: PalmitoTransitLine): string | undefined => {
  if (line.between === null) {
    return undefined
  }
  const to = inParis(line.between.to)
  return to.isValid ? `dernier scan à Mouguerre le ${to.toFormat('dd/LL HH:mm')}` : undefined
}

const transitCount = (line: PalmitoTransitLine) => (line.count === 0 ? '—' : line.count)

function StoreStocksTab() {
  const navigate = Route.useNavigate()
  const { storeId } = Route.useParams()

  const [stockSearch, setStockSearch] = useState('')

  const stock = useStoreStock(storeId)
  const transit = stock.data?.transit
  const transitRole = transit?.role

  const columns = useMemo(() => {
    if (transitRole === undefined) {
      return stockColDef
    }
    const afterStock = stockColDef.findIndex((column) => column.field === 'stock') + 1
    return [
      ...stockColDef.slice(0, afterStock),
      ...transitColDefs(transitRole),
      ...stockColDef.slice(afterStock),
    ]
  }, [transitRole])

  const stockRows = useMemo(() => {
    if (stock.data === undefined) {
      return []
    }
    return stock.data.rows.filter((row) => includesLowerCase(row.label, stockSearch))
  }, [stock.data, stockSearch])

  return (
    <StoreDetailsLayout storeId={storeId} tab="stocks">
      <div style={{ display: 'flex' }}>
        <StockCard
          mr
          flex
          title="NB D'ARTICLES TOTAL EN STOCK"
          content={stock.data?.metadata.articleCount}
        />
        <StockCard
          mr
          flex
          title="NB D'ARTICLES ANTI GASPI EN STOCK"
          content={stock.data?.metadata.antiGaspiArticleCount}
        />

        <StockCard
          flex
          title="NB D'ARTICLES EXPIRES EN STOCK"
          content={stock.data?.metadata.expiredArticleCount}
        />
      </div>
      <div style={{ padding: 10 }} />
      <div style={{ display: 'flex' }}>
        <StockCard
          mr
          flex
          title="VALORISATION TOTALE DES ARTICLES EN STOCK"
          content={priceToString(stock.data?.metadata.totalStockValue ?? 0)}
        />
        <StockCard
          mr
          flex
          title="VALORISATION DES ARTICLES ANTI GASPI EN STOCK"
          content={priceToString(stock.data?.metadata.totalAntiGaspiStockValue ?? 0)}
        />

        <StockCard
          flex
          title="VALORISATION TOTALE DES ARTICLES EXPIRES EN STOCK"
          content={priceToString(stock.data?.metadata.totalExpiredStockValue ?? 0)}
        />
      </div>
      {transit && (
        <>
          <div style={{ padding: 10 }} />
          {transit.role === 'arrival' ? (
            <div style={{ display: 'flex' }}>
              <StockCard
                mr
                flex
                title="EN TRANSPORT VERS LA BOUTIQUE (HORS TOTAL)"
                content={transitCount(transit.inbound)}
                subtitle={flashedSubtitle(transit.inbound)}
              />
              <StockCard
                mr
                flex
                title="DONT EN RÉSERVE À MOUGUERRE"
                content={transitCount(transit.reserve)}
                subtitle={lastScanSubtitle(transit.reserve)}
              />
              <div style={{ flex: 1 }} />
            </div>
          ) : (
            <div style={{ display: 'flex' }}>
              <StockCard
                mr
                flex
                title="RÉSERVE BIARRITZ (HORS STOCK MOUGUERRE)"
                content={transitCount(transit.reserve)}
                subtitle={lastScanSubtitle(transit.reserve)}
              />
              <div style={{ flex: 1, marginRight: '24px' }} />
              <div style={{ flex: 1 }} />
            </div>
          )}
        </>
      )}
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            padding: '16px',
          }}
        >
          <TextField
            variant="outlined"
            label="Recherche"
            value={stockSearch}
            onChange={(e) => setStockSearch(e.target.value)}
          />
          <div style={{ flexGrow: 1 }} />
        </div>
        <SimpleDataGrid
          columns={columns}
          rows={stockRows}
          loading={stock.isLoading}
          checkboxSelection={false}
          sx={DataGridOverrides.wrapHeaderNames}
          onRowClick={(row) =>
            navigate({
              to: '/stores/$storeId/stocks/product/$productId',
              params: { storeId, productId: row.id as number },
            })
          }
        />
      </Paper>
    </StoreDetailsLayout>
  )
}
